// Reimbursement lifecycle, as the member sees it. Shared by ReimbursementSection, ReimbursementCard,
// ReimbursementHistory and the forms.
//
// The status values are a cross-repo contract, duplicated value for value in toothlens-APIs
// (app/utils/reimbursementLifecycle), insurance-backend (app/models/reimbursement/reimbursementStatus.js)
// and insurance-frontend (src/Components/ReimbursementDashboard/status.ts). Change all four together;
// reimbursementStatus.spec.js pins the list.
//
// Every status label says "Reimbursement" so members never mistake this for the clinic prescription
// flow. Tracker steps are short, since the status pill above them already says it.
//
// Tone classes are written out in full (never built as `bg-portal-${x}`), so Tailwind's content scan
// keeps them.

export const REIMBURSEMENT_STATUSES = [
    'reimbursement_prescription_review',
    'reimbursement_prescription_reupload_requested',
    'reimbursement_prescription_rejected',
    'reimbursement_treatment_pending',
    'reimbursement_treatment_review',
    'reimbursement_treatment_reupload_requested',
    'reimbursement_treatment_rejected',
    'reimbursement_completed',
];

export const PRESCRIPTION_APPROVED_LABEL = 'Reimbursement Prescription Approved';

// What the member sees for each status. reimbursement_treatment_pending (approved, waiting for the
// member's proof of treatment) reads as "Prescription Approved" here; reviewers still see it as
// "Treatment Pending" on the dashboard.
export const STATUS_LABEL = {
    reimbursement_prescription_review: 'Reimbursement Prescription Review',
    reimbursement_prescription_reupload_requested: 'Reimbursement Prescription Re-upload Requested',
    reimbursement_prescription_rejected: 'Reimbursement Prescription Rejected',
    reimbursement_treatment_pending: PRESCRIPTION_APPROVED_LABEL,
    reimbursement_treatment_review: 'Reimbursement Treatment Review',
    reimbursement_treatment_reupload_requested: 'Reimbursement Treatment Re-upload Requested',
    reimbursement_treatment_rejected: 'Reimbursement Treatment Rejected',
    reimbursement_completed: 'Reimbursement Completed',
};

// Pill / panel colours: light fill, border, and a darker ink that passes WCAG AA on the fill.
export const TONE = {
    review: 'bg-portal-purple-light border-portal-purple-border text-portal-purple-dark',
    action: 'bg-portal-orange-light border-portal-orange-border text-portal-orange-ink',
    success: 'bg-portal-green-light border-portal-green-border text-portal-green-ink',
    danger: 'bg-red-50 border-red-200 text-red-700',
    neutral: 'bg-portal-bg border-portal-border text-portal-sub',
};

export const STATUS_STYLE = {
    reimbursement_prescription_review: TONE.review,
    reimbursement_prescription_reupload_requested: TONE.action,
    reimbursement_prescription_rejected: TONE.danger,
    reimbursement_treatment_pending: TONE.success,
    reimbursement_treatment_review: TONE.review,
    reimbursement_treatment_reupload_requested: TONE.action,
    reimbursement_treatment_rejected: TONE.danger,
    reimbursement_completed: TONE.success,
};

export const statusLabel = (status) => STATUS_LABEL[status] || 'Reimbursement';
export const statusStyle = (status) => STATUS_STYLE[status] || TONE.neutral;

export const TERMINAL_STATUSES = [
    'reimbursement_prescription_rejected',
    'reimbursement_treatment_rejected',
    'reimbursement_completed',
];

export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(status);

// Which Reimbursements get a full status card. Every open one does, since each may need the
// member's action and a member may have several. The latest one keeps its card after it closes,
// so its outcome and reviewer note stay in view.
export const reimbursementCards = (open = [], latest = null) => (
    latest && !open.some((r) => r._id === latest._id) ? [...open, latest] : open
);

// The Reimbursements with approved procedures waiting for proof of treatment — where the
// dashboard's "Upload proof of treatment" lands.
export const awaitingProof = (reimbursements = []) => (
    reimbursements.filter((r) => r.reimbursement_status === 'reimbursement_treatment_pending')
);

export const formatInr = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// "14 Oct 2026", or '' when there is no usable date. `calendarDay` is for date-only values
// (treatment_completion_date is stored as UTC midnight), so they never shift a day by time zone.
export const formatDate = (value, { calendarDay = false } = {}) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', ...(calendarDay ? { timeZone: 'UTC' } : {}),
    });
};

const countOf = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

// --- Uploads ----------------------------------------------------------------------------------

// Model field → the upload endpoint's `field` name. Array fields are several upload slots, sent in
// slot order (pre_op_xray_urls 2; a proof's optional X-rays and supporting documents 5 each).
// post_treatment_photo_url is the single photo of proofs filed before the three-photo proof; it is
// kept so an old proof can still be shown and re-uploaded.
const slots = (prefix, count) => Array.from({ length: count }, (_, i) => `${prefix}_${i + 1}`);

export const UPLOAD_FIELD = {
    reimbursement_approval_file_url: 'prescription_file',
    upper_occlusal_photo_url: 'upper_occlusal_photo',
    lower_occlusal_photo_url: 'lower_occlusal_photo',
    problem_area_photo_url: 'problem_area_photo',
    additional_photo_url: 'additional_photo',
    pre_op_xray_urls: ['pre_op_xray_1', 'pre_op_xray_2'],
    invoice_receipt_url: 'invoice_receipt',
    post_treatment_upper_occlusal_photo_url: 'post_treatment_upper_occlusal_photo',
    post_treatment_lower_occlusal_photo_url: 'post_treatment_lower_occlusal_photo',
    post_treatment_problem_area_photo_url: 'post_treatment_problem_area_photo',
    post_treatment_photo_url: 'post_treatment_photo',
    post_treatment_xray_urls: slots('post_treatment_xray', 5),
    supporting_document_urls: slots('supporting_document', 5),
};

export const FIELD_LABEL = {
    reimbursement_approval_file_url: 'Prescription',
    upper_occlusal_photo_url: 'Upper Occlusal photo',
    lower_occlusal_photo_url: 'Lower Occlusal photo',
    problem_area_photo_url: 'Problem Area photo',
    additional_photo_url: 'Additional photo',
    pre_op_xray_urls: 'Pre-Op X-rays',
    invoice_receipt_url: 'Invoice / receipt',
    post_treatment_upper_occlusal_photo_url: 'Upper Occlusal photo (after treatment)',
    post_treatment_lower_occlusal_photo_url: 'Lower Occlusal photo (after treatment)',
    post_treatment_problem_area_photo_url: 'Problem Area photo (after treatment)',
    post_treatment_photo_url: 'Post-treatment photo',
    post_treatment_xray_urls: 'X-rays (after treatment)',
    supporting_document_urls: 'Supporting documents',
};

export const fieldLabel = (field) => FIELD_LABEL[field] || field;

// Proof-of-treatment file field → where the API keeps the name the member picked (display only).
export const FILE_NAME_FIELD = {
    invoice_receipt_url: 'invoice_receipt_name',
    post_treatment_upper_occlusal_photo_url: 'post_treatment_upper_occlusal_photo_name',
    post_treatment_lower_occlusal_photo_url: 'post_treatment_lower_occlusal_photo_name',
    post_treatment_problem_area_photo_url: 'post_treatment_problem_area_photo_name',
    post_treatment_photo_url: 'post_treatment_photo_name',
};

// What each upload slot accepts. The server allows JPG/JPEG/PNG for photos (toothlens-APIs
// IMAGE_RULES) and more document types than these for the prescription, invoice and supporting
// documents; the portal asks for PDF/JPG/PNG there. maxMb mirrors the server's MAX_IMAGE_SIZE.
export const UPLOAD_RULES = {
    document: {
        extensions: ['pdf', 'jpg', 'jpeg', 'png'],
        mimeTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
        maxMb: 50,
        accept: '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png',
        hint: 'PDF, JPG, JPEG or PNG · up to 50 MB',
        wrongType: 'Please upload a PDF, JPG, JPEG or PNG file.',
    },
    image: {
        extensions: ['jpg', 'jpeg', 'png'],
        mimeTypes: ['image/jpeg', 'image/jpg', 'image/png'],
        maxMb: 50,
        accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
        hint: 'JPG, JPEG or PNG · up to 50 MB',
        wrongType: 'Please upload a JPG, JPEG or PNG photo.',
    },
};

const DOCUMENT_UPLOADS = ['prescription_file', 'invoice_receipt', ...UPLOAD_FIELD.supporting_document_urls];
export const uploadKind = (uploadField) => (DOCUMENT_UPLOADS.includes(uploadField) ? 'document' : 'image');
export const acceptFor = (uploadField) => UPLOAD_RULES[uploadKind(uploadField)].accept;

// Why a picked file cannot be uploaded to a slot of this kind, or null when it can. Checked before
// uploading, so the member hears about it straight away.
export const fileProblem = (file, kind = 'document') => {
    const rules = UPLOAD_RULES[kind] || UPLOAD_RULES.document;
    if (!file) return 'Please choose a file.';
    const extension = String(file.name || '').includes('.') ? String(file.name).split('.').pop().toLowerCase() : '';
    if (['heic', 'heif'].includes(extension) || /hei[cf]/i.test(file.type || '')) {
        return 'HEIC photos are not supported. Please choose a JPG or PNG.';
    }
    if (!rules.extensions.includes(extension) || (file.type && !rules.mimeTypes.includes(file.type))) {
        return rules.wrongType;
    }
    if (file.size > rules.maxMb * 1024 * 1024) return `This file is larger than ${rules.maxMb} MB.`;
    if (!file.size) return 'This file is empty.';
    return null;
};

export const formatFileSize = (bytes) => {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// --- Recommended procedures --------------------------------------------------------------------
// The treatment plan a ToothLens reviewer transcribed from the member's prescription, each
// procedure approved (for an amount) or rejected (with a reason) one by one. The member sees each
// procedure's current decision as soon as it is made.

export const approvedProcedures = (procedures = []) => procedures.filter((p) => p.decision === 'approved');

const sumApproved = (procedures = []) => procedures.reduce((sum, p) => sum + (Number(p.approved_amount) || 0), 0);

// Whether the status card lists the procedures: whenever the reviewer has tabulated any, in every
// status, each with its own decision.
export const showsProcedures = (reimbursement) => (reimbursement?.procedures || []).length > 0;

// The server sends approved_total; computed here too for older responses.
export const approvedTotalOf = (reimbursement) => (typeof reimbursement?.approved_total === 'number'
    ? reimbursement.approved_total
    : sumApproved(approvedProcedures(reimbursement?.procedures)));

// "Tooth 36", but "Full Mouth" / "Quadrant 2" as they are.
export const toothLabel = (tooth) => (/^\d+$/.test(String(tooth)) ? `Tooth ${tooth}` : String(tooth || ''));

// Procedures still waiting for a decision, once the reviewer has started deciding (while nothing is
// decided yet, "still under review" says nothing the status does not).
export const undecidedProcedures = (reimbursement) => {
    const procedures = reimbursement?.procedures || [];
    if (!procedures.some((p) => p.decision && p.decision !== 'pending')) return [];
    return procedures.filter((p) => !p.decision || p.decision === 'pending');
};

// --- Proof of treatment, in rounds -------------------------------------------------------------
// One proof covers every approved procedure that has none yet; the server decides which. Only one
// proof is under review at a time, so a Reimbursement may collect several proofs over time.

export const PROOF_STATUS_LABEL = {
    awaiting_proof: 'Awaiting your proof',
    in_review: 'Under review',
    reupload_requested: 'Re-upload requested',
    accepted: 'Accepted',
    rejected: 'Not accepted',
};

export const PROOF_STATUS_STYLE = {
    awaiting_proof: TONE.action,
    in_review: TONE.review,
    reupload_requested: TONE.action,
    accepted: TONE.success,
    rejected: TONE.danger,
};

// Older APIs send no per-procedure proof state: the one proof's state follows the Reimbursement.
const LEGACY_PROOF_STATUS = {
    reimbursement_treatment_pending: 'awaiting_proof',
    reimbursement_treatment_review: 'in_review',
    reimbursement_treatment_reupload_requested: 'reupload_requested',
    reimbursement_completed: 'accepted',
    reimbursement_treatment_rejected: 'rejected',
};

const hasProofState = (reimbursement) => (reimbursement?.procedures || []).some((p) => Object.prototype.hasOwnProperty.call(p, 'proof_status'));

// An approved procedure's proof state ('awaiting_proof' | 'in_review' | 'reupload_requested' |
// 'accepted' | 'rejected'), or null when it is not approved or its state is unknown.
export const procedureProofStatus = (procedure, reimbursement) => {
    if (procedure?.decision !== 'approved') return null;
    if (procedure.proof_status) return procedure.proof_status;
    if (hasProofState(reimbursement)) return null;
    return LEGACY_PROOF_STATUS[reimbursement?.reimbursement_status] || null;
};

// The proofs submitted so far, oldest first. Built from treatment_proofs; an older API sends only
// the current proof (treatment_proof), which then reads as proof #1 with the Reimbursement's state.
export const proofRounds = (reimbursement) => {
    if (!reimbursement) return [];
    if (Array.isArray(reimbursement.treatment_proofs)) {
        return reimbursement.treatment_proofs
            .map((proof, index) => ({ ...proof, round: Number(proof.round) || index + 1 }))
            .sort((a, b) => a.round - b.round);
    }
    const proof = reimbursement.treatment_proof;
    if (!proof) return [];
    const status = LEGACY_PROOF_STATUS[reimbursement.reimbursement_status] || 'in_review';
    const approved = approvedProcedures(reimbursement.procedures);
    return [{
        ...proof,
        round: 1,
        review_status: status,
        submitted_at: proof.createdAt || null,
        decided_at: null,
        decision_note: ['rejected', 'reupload_requested'].includes(status) ? (reimbursement.review_note || null) : null,
        procedures: approved.map(({ _id, tooth, treatment, approved_amount: amount }) => ({ _id, tooth, treatment, approved_amount: amount })),
        approved_amount: approved.length ? approvedTotalOf(reimbursement) : null,
    }];
};

// The round the latest proof is, or 0 before any.
const latestRound = (rounds) => (rounds.length ? rounds[rounds.length - 1].round : 0);

// The approved procedures waiting for proof, which the member picks from for a proof (the
// treatments they have had; the rest wait for a later proof): the server's next_proof, else the
// procedures awaiting proof, else (older API) every approved one. `planned` is false for a
// Reimbursement approved before treatment plans existed: its proof covers the approved treatment
// as a whole, so there is nothing to pick.
export const proofChoices = (reimbursement) => {
    const procedures = reimbursement?.procedures || [];
    const next = reimbursement?.next_proof;
    let choices;
    if (next && Array.isArray(next.procedure_ids)) {
        const ids = next.procedure_ids.map(String);
        choices = procedures.filter((p) => ids.includes(String(p._id)));
    } else if (hasProofState(reimbursement)) {
        choices = procedures.filter((p) => p.decision === 'approved' && p.proof_status === 'awaiting_proof');
    } else {
        choices = approvedProcedures(procedures);
    }
    return { planned: procedures.length > 0, procedures: choices, total: sumApproved(choices) };
};

// The approved total of the chosen procedures (ids as strings).
export const selectedTotal = (procedures = [], ids = []) => sumApproved(procedures.filter((p) => ids.includes(String(p._id))));

// The total accepted so far: the server's accepted_total, else the approved amounts of procedures
// whose proof was accepted.
export const acceptedTotalOf = (reimbursement) => {
    if (typeof reimbursement?.accepted_total === 'number') return reimbursement.accepted_total;
    return sumApproved((reimbursement?.procedures || []).filter((p) => procedureProofStatus(p, reimbursement) === 'accepted'));
};

// The documents of one proof, with the name the member picked (older proofs have none, and the
// optional X-rays and supporting documents never do, so the document type stands in). Only the ones
// present: older proofs have one photo, newer ones three, plus any optional files. `index` is the
// file's place in an array field (for its signed link).
const PROOF_DOCUMENTS = [
    ['invoice_receipt_url', 'Invoice / receipt'],
    ['post_treatment_upper_occlusal_photo_url', 'Upper Occlusal'],
    ['post_treatment_lower_occlusal_photo_url', 'Lower Occlusal'],
    ['post_treatment_problem_area_photo_url', 'Problem Area'],
    ['post_treatment_photo_url', 'Post-treatment photo'],
];
const PROOF_DOCUMENT_LISTS = [
    ['post_treatment_xray_urls', 'X-ray'],
    ['supporting_document_urls', 'Supporting document'],
];

export const proofDocuments = (proof) => {
    if (!proof) return [];
    const single = PROOF_DOCUMENTS
        .filter(([field]) => proof[field])
        .map(([field, label]) => ({ field, index: 0, label, name: proof[FILE_NAME_FIELD[field]] || label, url: proof[field] }));
    const lists = PROOF_DOCUMENT_LISTS.flatMap(([field, noun]) => (Array.isArray(proof[field]) ? proof[field] : [])
        .map((url, index) => ({ field, index, label: `${noun} ${index + 1}`, name: `${noun} ${index + 1}`, url }))
        .filter((doc) => doc.url));
    return [...single, ...lists];
};

// "UPI · name@bank" or "Bank account · Holder · ••••1234 · IFSC" (the API masks the number).
export const payoutLabel = (proof) => {
    if (!proof) return '';
    if (proof.payout_method === 'upi') return ['UPI', proof.upi_id].filter(Boolean).join(' · ');
    if (proof.payout_method === 'bank') {
        return ['Bank account', proof.bank_account_holder_name, proof.bank_account_number, proof.bank_ifsc].filter(Boolean).join(' · ');
    }
    return '';
};

// --- Each procedure's own status -----------------------------------------------------------------
// Every procedure shows its own current decision, whatever the others' and whatever the
// Reimbursement's status: Approved, Rejected or Pending. An approved one also says where its proof of
// treatment stands.

const DECISION_STATUS = {
    approved: { key: 'approved', label: 'Approved', tone: TONE.success },
    rejected: { key: 'rejected', label: 'Rejected', tone: TONE.danger },
    pending: { key: 'pending', label: 'Pending', tone: TONE.review },
};

const PROOF_LINE = {
    in_review: (round) => ({ label: `Proof #${round} under review`, tone: TONE.review }),
    reupload_requested: (round) => ({ label: `Proof #${round}: re-upload requested`, tone: TONE.action }),
    accepted: (round) => ({ label: `Proof #${round} accepted`, tone: TONE.success }),
    rejected: (round) => ({ label: `Proof #${round} not accepted`, tone: TONE.danger }),
};

// An approved procedure without proof can be proved now only while the Reimbursement waits for a
// proof. While another proof is being reviewed (one at a time, ADR-0009) it says when it can.
const awaitingProofLine = (reimbursement) => {
    const status = reimbursement?.reimbursement_status;
    if (status !== 'reimbursement_treatment_review' && status !== 'reimbursement_treatment_reupload_requested') {
        return { label: 'Awaiting your proof', tone: TONE.action };
    }
    const open = proofRounds(reimbursement).find((r) => r.review_status === 'in_review' || r.review_status === 'reupload_requested');
    return {
        label: open ? `Upload after Proof #${open.round} is reviewed` : 'Upload after the current proof is reviewed',
        tone: TONE.neutral,
    };
};

// { key, label, tone, proof: { label, tone } | null } for one procedure.
export const procedureStatusOf = (procedure, reimbursement) => {
    const status = DECISION_STATUS[procedure?.decision] || DECISION_STATUS.pending;
    const proofStatus = procedureProofStatus(procedure, reimbursement);
    if (proofStatus === 'awaiting_proof') return { ...status, proof: awaitingProofLine(reimbursement) };
    const line = proofStatus ? PROOF_LINE[proofStatus] : null;
    return { ...status, proof: line ? line(Number(procedure.proof_round) || 1) : null };
};

// --- The progress tracker ------------------------------------------------------------------------

export const trackerLabels = (round = 1) => [
    'Prescription review',
    'Approved',
    round > 1 ? `Proof #${round}` : 'Proof of treatment',
    round > 1 ? `Proof #${round} review` : 'Treatment review',
    'Completed',
];

export const STEPS = trackerLabels(1);

// For each status: how far along the tracker it is, and whether the current step failed.
const PROGRESS = {
    reimbursement_prescription_review: { current: 0 },
    reimbursement_prescription_reupload_requested: { current: 0, attention: true },
    reimbursement_prescription_rejected: { current: 0, failed: true },
    reimbursement_treatment_pending: { current: 2 },
    reimbursement_treatment_review: { current: 3 },
    reimbursement_treatment_reupload_requested: { current: 3, attention: true },
    reimbursement_treatment_rejected: { current: 3, failed: true },
    reimbursement_completed: { current: 4, done: true },
};

// Which proof the tracker follows: the one under review, or the next one to upload (also while
// the remaining procedures are reviewed). A closed Reimbursement shows the plain labels; its proofs
// are listed below the tracker.
export const trackerRound = (reimbursement) => {
    const status = reimbursement?.reimbursement_status;
    if (!status || isTerminalStatus(status)) return 1;
    const rounds = proofRounds(reimbursement);
    if (status === 'reimbursement_treatment_review' || status === 'reimbursement_treatment_reupload_requested') {
        return latestRound(rounds) || 1;
    }
    return latestRound(rounds) + 1;
};

// [{ label, state: 'done' | 'current' | 'attention' | 'failed' | 'upcoming' }]
export const trackerSteps = (status, round = 1) => {
    const progress = PROGRESS[status] || { current: 0 };
    return trackerLabels(round).map((label, index) => {
        let state = 'upcoming';
        if (index < progress.current || (progress.done && index === progress.current)) state = 'done';
        else if (index === progress.current) state = progress.failed ? 'failed' : progress.attention ? 'attention' : 'current';
        return { label, state };
    });
};

// --- The one next-step block on each card ----------------------------------------------------------
// { tone, title, body, notes[], documents[], reviewerNote, action, actionLabel }. `action` drives the
// card's single button: 'upload_proof' | 'reupload' | null (nothing to do but wait, or closed —
// starting a new Reimbursement is offered once for the whole section).

const block = (fields) => ({
    tone: 'review', notes: [], documents: [], reviewerNote: null, action: null, actionLabel: null, ...fields,
});

const CLOSED_BODY = 'This reimbursement is closed. You can start a new reimbursement request for another treatment.';

const undecidedNote = (count) => (count
    ? `${countOf(count, 'procedure')} from your prescription ${count === 1 ? 'is' : 'are'} still under review. You can add proof for ${count === 1 ? 'it once it is' : 'them once they are'} approved.`
    : null);

// Approved procedures the proof under review does not cover: approved meanwhile, or left out of it.
const stillAwaitingNote = (count) => (count
    ? `${countOf(count, 'approved procedure')} ${count === 1 ? 'is' : 'are'} still waiting for your proof. You can upload proof for ${count === 1 ? 'it' : 'them'} after this review.`
    : null);

export const nextAction = (reimbursement) => {
    if (!reimbursement) return null;
    const status = reimbursement.reimbursement_status;
    const rounds = proofRounds(reimbursement);
    const last = rounds[rounds.length - 1] || null;
    const request = reimbursement.document_request || null;
    const undecided = undecidedProcedures(reimbursement).length;
    const awaiting = (reimbursement.procedures || []).filter((p) => p.decision === 'approved' && p.proof_status === 'awaiting_proof').length;
    const reupload = request
        ? { documents: (request.fields || []).map(fieldLabel), reviewerNote: request.note || null, action: 'reupload', actionLabel: 'Re-upload documents' }
        : { reviewerNote: reimbursement.review_note || null };

    switch (status) {
        case 'reimbursement_prescription_review': {
            if (!rounds.length) {
                return block({
                    title: 'We are reviewing your reimbursement prescription',
                    body: 'Our team is checking your prescription and photos. This page updates as soon as it has been reviewed. No action is needed from you right now.',
                });
            }
            const outcome = { accepted: 'was accepted', rejected: 'was not accepted' }[last.review_status];
            return block({
                title: 'We are reviewing your remaining procedures',
                body: undecided
                    ? `${countOf(undecided, 'procedure')} from your prescription ${undecided === 1 ? 'is' : 'are'} still under review. If ${undecided === 1 ? 'it is' : 'they are'} approved, you can upload proof for ${undecided === 1 ? 'it' : 'them'} here.`
                    : 'Our team is reviewing the rest of your prescription. No action is needed from you right now.',
                notes: outcome ? [`Proof #${last.round} ${outcome}.`] : [],
            });
        }
        case 'reimbursement_prescription_reupload_requested':
            return block({
                tone: 'action',
                title: 'Please re-upload some documents',
                body: 'Our reviewer needs new copies of the documents listed below before your prescription can be approved.',
                ...reupload,
            });
        case 'reimbursement_prescription_rejected':
            return block({
                tone: 'danger',
                title: 'Your reimbursement prescription was not approved',
                body: CLOSED_BODY,
                reviewerNote: reimbursement.review_note || null,
            });
        case 'reimbursement_treatment_pending': {
            // A proof covers the approved procedures the member has had; the rest wait for a later one.
            const round = latestRound(rounds) + 1;
            const choices = proofChoices(reimbursement);
            const count = choices.procedures.length;
            const action = { action: 'upload_proof', actionLabel: round > 1 ? `Upload proof #${round}` : 'Upload proof of treatment' };
            const notes = [undecidedNote(undecided)].filter(Boolean);
            if (!count) {
                // Approved before treatment plans: one proof for the approved treatment.
                const total = approvedTotalOf(reimbursement);
                return block({
                    tone: 'action',
                    title: 'Upload your proof of treatment',
                    body: `${total > 0 ? `Your prescription has been approved for reimbursement of ${formatInr(total)}.` : 'Your prescription has been approved for reimbursement.'} Please upload your proof of treatment after completing the treatment.`,
                    notes,
                    ...action,
                });
            }
            const waiting = round === 1
                ? `Your prescription has been approved for reimbursement of ${formatInr(choices.total)} (${countOf(count, 'procedure')}).`
                : `${countOf(count, 'approved procedure')} ${count === 1 ? 'is' : 'are'} waiting for your proof (${formatInr(choices.total)}).`;
            const how = count > 1
                ? "After your treatment, upload proof for the ones you've had — you can add the others on a later proof."
                : 'Upload your proof once you have had the treatment.';
            return block({
                tone: 'action',
                title: "Upload proof for the treatments you've had",
                body: `${waiting} ${how}`,
                notes,
                ...action,
            });
        }
        case 'reimbursement_treatment_review': {
            const round = latestRound(rounds) || 1;
            return block({
                title: round > 1 ? `Proof #${round} submitted` : 'Proof of treatment submitted',
                body: "Our reimbursement team is reviewing it. No action is needed from you right now — we'll update you here once the review is complete.",
                notes: [stillAwaitingNote(awaiting), undecided ? `${countOf(undecided, 'procedure')} from your prescription ${undecided === 1 ? 'is' : 'are'} still under review.` : null].filter(Boolean),
            });
        }
        case 'reimbursement_treatment_reupload_requested': {
            const round = latestRound(rounds) || 1;
            return block({
                tone: 'action',
                title: round > 1 ? `Please re-upload some documents for proof #${round}` : 'Please re-upload some treatment documents',
                body: 'Our reviewer needs new copies of the documents listed below before your proof of treatment can be reviewed.',
                notes: [stillAwaitingNote(awaiting)].filter(Boolean),
                ...reupload,
            });
        }
        case 'reimbursement_treatment_rejected':
            return block({
                tone: 'danger',
                title: rounds.length > 1 ? 'Your proofs of treatment were not accepted' : 'Your proof of treatment was not approved',
                body: CLOSED_BODY,
                reviewerNote: reimbursement.review_note || null,
            });
        case 'reimbursement_completed': {
            const total = acceptedTotalOf(reimbursement) || approvedTotalOf(reimbursement);
            const payout = 'Our team will pay it out to the account you provided.';
            const body = rounds.length > 1
                ? `All ${rounds.length} proofs of treatment have been reviewed, and ${formatInr(total)} was accepted for reimbursement. ${payout}`
                : `${total > 0 ? `Your reimbursement of ${formatInr(total)} has been approved.` : 'Your reimbursement has been approved.'} ${payout}`;
            return block({
                tone: 'success',
                title: 'Reimbursement completed',
                body,
                notes: rounds.length > 1 ? rounds.filter((r) => r.review_status === 'rejected').map((r) => `Proof #${r.round} was not accepted.`) : [],
            });
        }
        default:
            return null;
    }
};

// Where the dashboard's "Upload proof of treatment" lands: the one Reimbursement waiting for proof,
// or the list with a hint saying why there is nothing to open. { open: reimbursement | null, hint }
export const proofLanding = (reimbursements = []) => {
    const waiting = awaitingProof(reimbursements);
    if (waiting.length === 1) return { open: waiting[0], hint: '' };
    if (waiting.length > 1) return { open: null, hint: 'Choose the reimbursement you want to upload proof of treatment for.' };
    const has = (status) => reimbursements.filter((r) => r.reimbursement_status === status);
    if (has('reimbursement_treatment_reupload_requested').length) {
        return { open: null, hint: 'Our reviewer asked for new copies of some treatment documents. Use “Re-upload documents” below.' };
    }
    const inReview = has('reimbursement_treatment_review');
    if (inReview.length) {
        const more = inReview.some((r) => (r.procedures || []).some((p) => p.decision === 'approved' && p.proof_status === 'awaiting_proof'));
        return {
            open: null,
            hint: more
                ? 'Your proof of treatment is being reviewed. You can upload proof for your other approved procedures once that review is done.'
                : "Your proof of treatment is being reviewed. We'll update you here once it has been reviewed.",
        };
    }
    return { open: null, hint: 'Proof of treatment can be uploaded once your prescription is approved.' };
};

// What a re-upload request replaces: the prescription itself, or the current proof (by claim id).
// { stage, proof, claimId }, or null when nothing was requested.
export const reuploadTarget = (reimbursement) => {
    const request = reimbursement?.document_request;
    if (!request) return null;
    if (request.stage === 'treatment') {
        const proof = reimbursement.treatment_proof || {};
        return { stage: 'treatment', proof, claimId: reimbursement.current_claim_id || proof._id || null };
    }
    return { stage: 'prescription', proof: reimbursement, claimId: null };
};

// --- Activity log --------------------------------------------------------------------------------

export const HISTORY_LABEL = {
    submit_prescription: 'Reimbursement requested',
    reupload_prescription: 'You re-uploaded prescription documents',
    approve_prescription: PRESCRIPTION_APPROVED_LABEL,
    approve_more_procedures: 'More procedures approved',
    reject_prescription: STATUS_LABEL.reimbursement_prescription_rejected,
    request_prescription_reupload: STATUS_LABEL.reimbursement_prescription_reupload_requested,
    submit_treatment_proof: 'You uploaded proof of treatment',
    reupload_treatment: 'You re-uploaded treatment documents',
    accept_treatment_proof: 'Proof of treatment accepted',
    reject_treatment_proof: 'Proof of treatment not accepted',
    complete: STATUS_LABEL.reimbursement_completed,
    reject_treatment: STATUS_LABEL.reimbursement_treatment_rejected,
    request_treatment_reupload: STATUS_LABEL.reimbursement_treatment_reupload_requested,
    backfill: 'Moved to the new reimbursement tracker',
    update_procedures: 'Treatment plan updated by ToothLens',
    decide_procedures: 'Procedure decisions updated',
};

// Labels for entries about a later proof, which name it.
const ROUND_HISTORY_LABEL = {
    submit_treatment_proof: (n) => `You uploaded proof #${n}`,
    reupload_treatment: (n) => `You re-uploaded documents for proof #${n}`,
    accept_treatment_proof: (n) => `Proof #${n} accepted`,
    reject_treatment_proof: (n) => `Proof #${n} not accepted`,
    request_treatment_reupload: (n) => `Re-upload requested for proof #${n}`,
};

// `entry.round` is the proof the entry is about (the card looks it up from entry.claim_id).
export const historyLabel = (action, entry = {}) => {
    if (entry.round > 1 && ROUND_HISTORY_LABEL[action]) return ROUND_HISTORY_LABEL[action](entry.round);
    if (HISTORY_LABEL[action]) return HISTORY_LABEL[action];
    return entry.role === 'reviewer' ? 'Updated by ToothLens' : 'Updated';
};
